// @view/workspace/Clients.tsx
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as styles from "@styles/ClientsPage.css";
import "@styles/util/ClientsPage.global.css";
import { Pencil, Trash2 } from "lucide-react";
import type { OptionTs, IOts } from "@ps-bridge/interop";
import { makeDocumentUploadFileName } from "@ps-bridge/models/brands/documentUploadFileName";
import { registerDocumentUploadFile } from "@utils/documents/documentUploadRegistry";
import * as Option from "@utils/Option";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import * as IO from "@ps-bridge/interop/io";
import { ioEffect } from "@utils/effects/branded";
import ClientsPagePanel, {
  type ClientsPageActionPhase,
  type ClientsPageModalPhase,
  type ClientsPageView,
} from "@view/panels/workspace/ClientsPage.panel";
import DocumentsPanelView from "@view/workspace/clients/DocumentsPanel.view";
import ClientsPanelView from "@view/workspace/clients/ClientsPanel.view";
import ProjectsPanelView from "@view/workspace/clients/ProjectsPanel.view";
import { useIoState } from "@utils/useIoState";
import { useIoEffect } from "@hooks/effects/useIoEffect";
import { cleanupNone, cleanupSome, useIoEffectWithCleanup } from "@hooks/effects/useIoEffectWithCleanup";
import type { ClientsViewProps } from "@ps-bridge/contracts/clients";
import type { SupplyStatusResponse } from "@services/clients/DocumentAssistantSupplyApi";

type ClientsViewWithSupplyProps = ClientsViewProps & {
  clientCreationEnabled: boolean;
  supplyStatusRows: SupplyStatusResponse[];
  supplySelectedFileName: string;
  supplyErrorMessage: string;
  isSupplySubmitting: boolean;
  onSelectSupplyFile: (fileList: OptionTs<FileList>) => Promise<Ok>;
  onRefreshSupplyStatus: (supplyId: string) => Promise<Ok>;
};

const formatBytes = (value: number): string => {
  if (!Number.isFinite(value)) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let idx = 0;
  let size = value;
  while (size >= 1024 && idx < units.length - 1) {
    size /= 1024;
    idx += 1;
  }
  return `${size.toFixed(size >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
};

const formatTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

type ProjectMenu = {
  id: string;
  title: string;
  top: number;
  left: number;
};

type ClientItem = ClientsViewProps extends { clients: Array<infer Item> } ? Item : never;
type ProjectItem = ClientsViewProps extends { projects: Array<infer Item> } ? Item : never;
type DocumentItem = ClientsViewProps extends { documents: Array<infer Item> } ? Item : never;

type ProjectMenuDropdownProps = {
  menu: ProjectMenu;
  onRename: () => Ok;
  onDelete: () => Ok;
};

const ProjectMenuDropdown: React.FC<ProjectMenuDropdownProps> = ({ menu, onRename, onDelete }) => {
  const menuRef = useRef<HTMLDivElement>(document.createElement("div"));

  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node) {
      return;
    }
    node.style.top = `${menu.top}px`;
    node.style.left = `${menu.left}px`;
  }, [menu.left, menu.top]);

  return (
    <div
      ref={menuRef}
      className={styles.menuDropdown}
      role="menu"
      onClick={(event) => event.stopPropagation()}
    >
      <button className={styles.menuItem} onClick={onRename}>
        <Pencil size={16} className={styles.actionIcon} />
        Rename
      </button>
      <button className={styles.menuItemDanger} onClick={onDelete}>
        <Trash2 size={16} className={styles.actionIcon} />
        Delete
      </button>
    </div>
  );
};

const ClientsView: React.FC<ClientsViewWithSupplyProps> = ({
  clients,
  projects,
  documents,
  activeClientId,
  activeProjectId,
  newClientName,
  newProjectTitle,
  isUploading,
  onNewClientNameChange,
  onNewProjectTitleChange,
  onCreateClient,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onDeleteDocument,
  onUploadDocuments,
  onViewDocument,
  onDownloadDocument,
  onBackToClients,
  onBackToProjects,
  onNavigateClient,
  onNavigateProject,
  clientCreationEnabled,
  supplyStatusRows,
  supplySelectedFileName,
  supplyErrorMessage,
  isSupplySubmitting,
  onSelectSupplyFile,
  onRefreshSupplyStatus,
}) => {
  const { read: clientQuery, set: setClientQuery } = useIoState("");
  const { read: documentQuery, set: setDocumentQuery } = useIoState("");
  const [deleteTarget, setDeleteTarget] = useState<OptionTs<DocumentItem>>(Option.none());
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [projectMenu, setProjectMenu] = useState<OptionTs<ProjectMenu>>(Option.none());
  const [renameTarget, setRenameTarget] = useState<OptionTs<ProjectItem>>(Option.none());
  const [renameProjectTitle, setRenameProjectTitle] = useState("");
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<OptionTs<ProjectItem>>(Option.none());
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(document.createElement("input"));

  const openProjectMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, project: ProjectItem) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 180;
    const margin = 8;
    const left = Math.max(
      margin,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - margin)
    );
    const top = rect.bottom + 6;
    setProjectMenu(Option.some({ id: project.id, title: project.name, top, left }));
  }, []);

  const projectMenuListenerSetup = useMemo(
    () =>
      ioEffect(() => {
        if (Option.fold(projectMenu, () => true, () => false)) {
          return cleanupNone();
        }
        const closeMenu = () => setProjectMenu(Option.none());
        window.addEventListener("scroll", closeMenu, true);
        window.addEventListener("resize", closeMenu);
        window.addEventListener("click", closeMenu);
        return cleanupSome(ioEffect(() => {
          window.removeEventListener("scroll", closeMenu, true);
          window.removeEventListener("resize", closeMenu);
          window.removeEventListener("click", closeMenu);
          return ok;
        }));
      }),
    [projectMenu],
  );
  useIoEffectWithCleanup(projectMenuListenerSetup, [projectMenuListenerSetup]);

  const resetProjectMenuIo = useMemo(
    () =>
      ioEffect(() => {
        if (
          Option.fold(activeClientId, () => false, () => true) ||
          Option.fold(activeProjectId, () => false, () => true)
        ) {
          setProjectMenu(Option.none());
          return ok;
        }
        setProjectMenu(Option.none());
        return ok;
      }),
    [activeClientId, activeProjectId],
  );
  useIoEffect(resetProjectMenuIo, [resetProjectMenuIo]);

  const activeClient = useMemo(
    () =>
      Option.chain((clientId) => {
        const match = clients.find((client) => client.id === clientId);
        return match ? Option.some(match) : Option.none<ClientItem>();
      })(activeClientId),
    [clients, activeClientId],
  );

  const activeProject = useMemo(
    () =>
      Option.chain((projectId) => {
        const match = projects.find((project) => project.id === projectId);
        return match ? Option.some(match) : Option.none<ProjectItem>();
      })(activeProjectId),
    [projects, activeProjectId],
  );

  const activeClientName = useMemo(
    () => Option.fold(activeClient, () => "", (client: ClientItem) => client.name),
    [activeClient],
  );
  const activeProjectName = useMemo(
    () => Option.fold(activeProject, () => "Documents", (project: ProjectItem) => project.name),
    [activeProject],
  );
  const activeProjectLastEdited = useMemo(
    () => Option.fold(activeProject, () => "", (project: ProjectItem) => project.last_updated_at),
    [activeProject],
  );
  const hasActiveClient = Option.fold(activeClient, () => false, () => true);
  const hasActiveProject = Option.fold(activeProject, () => false, () => true);

  const filteredClients = useMemo(() => {
    if (!clientQuery.trim()) {
      return clients;
    }
    const query = clientQuery.trim().toLowerCase();
    return clients.filter((client) => client.name.toLowerCase().includes(query));
  }, [clients, clientQuery]);

  const filteredProjects = useMemo(() => {
    if (!clientQuery.trim()) {
      return projects;
    }
    const query = clientQuery.trim().toLowerCase();
    return projects.filter((project) => project.name.toLowerCase().includes(query));
  }, [projects, clientQuery]);

  const trimmedDocumentQuery = documentQuery.trim().toLowerCase();

  const filteredDocuments = useMemo(() => {
    if (!trimmedDocumentQuery) {
      return documents;
    }
    return documents.filter((doc) => doc.filename.toLowerCase().includes(trimmedDocumentQuery));
  }, [documents, trimmedDocumentQuery]);

  const projectNameOptions = useMemo(() => {
    const unique = new Set<string>();
    projects.forEach((project) => unique.add(project.name));
    return Array.from(unique);
  }, [projects]);

  const documentNameOptions = useMemo(() => {
    const unique = new Set<string>();
    documents.forEach((doc) => unique.add(doc.filename));
    return Array.from(unique);
  }, [documents]);

  const handlePresignAndUpload = useCallback(
    (fileList?: FileList): IOts<Ok> =>
      ioEffect(() => {
        if (!fileList || fileList.length === 0) {
          return ok;
        }
        const files = Array.from(fileList).map((file) => ({
          fileName: makeDocumentUploadFileName(file.name),
          fileSize: file.size,
          file: registerDocumentUploadFile(file),
        }));
        IO.run(onUploadDocuments({ files }));
        return ok;
      }),
    [onUploadDocuments],
  );

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      IO.run(handlePresignAndUpload(event.target.files ?? undefined));
      event.target.value = "";
    },
    [handlePresignAndUpload],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      IO.run(handlePresignAndUpload(event.dataTransfer.files));
    },
    [handlePresignAndUpload],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  }, []);

  const activeView: ClientsPageView = Option.fold(activeProjectId, () => false, () => true)
    ? "documents"
    : Option.fold(activeClientId, () => false, () => true)
      ? "projects"
      : "clients";

  const panelTitleText: Record<ClientsPageView, string> = {
    clients: "Your clients",
    projects: "Projects",
    documents: "Document Manager",
  };
  const panelHintText: Record<ClientsPageView, string> = {
    clients: `${clients.length} clients`,
    projects: "Select a project to see documents",
    documents: "Upload or remove files",
  };

  const createProjectButton = (
    <button
      className={styles.primaryButton}
      onClick={() => {
        IO.run(onNewProjectTitleChange(""));
        setIsCreateProjectModalOpen(true);
      }}
    >
      Create Project +
    </button>
  );
  const uploadButton = (
    <button
      className={isUploadPanelOpen ? styles.warningButton : styles.primaryButton}
      onClick={() => setIsUploadPanelOpen((current) => !current)}
    >
      {isUploadPanelOpen ? "Cancel" : "Add Documents +"}
    </button>
  );
  const addClientButton = (
    <button
      className={styles.primaryButton}
      onClick={() => {
        IO.run(onNewClientNameChange(""));
        setIsCreateClientModalOpen(true);
      }}
    >
      Add Client +
    </button>
  );

  const clientEmptyText = clientQuery ? "No matches found." : "No clients yet.";
  const projectEmptyText = clientQuery ? "No matches found." : "No projects yet.";

  const handleCreateClientView = (): Ok => {
    IO.run(onNewClientNameChange(""));
    setIsCreateClientModalOpen(true);
    return ok;
  };
  const handleCreateProjectView = (): Ok => {
    IO.run(onNewProjectTitleChange(""));
    setIsCreateProjectModalOpen(true);
    return ok;
  };
  const handleOpenProjectMenu = ({
    event,
    project,
  }: {
    event: React.MouseEvent<HTMLButtonElement>;
    project: ProjectItem;
  }): Ok => {
    event.stopPropagation();
    openProjectMenu(event, project);
    return ok;
  };
  const handleRemoveDocument = (doc: DocumentItem): Ok => {
    setDeleteTarget(Option.some(doc));
    return ok;
  };
  const runPanelIO = useCallback(<A,>(effect: IOts<A>): A => IO.run(effect), []);

  const clientsPanelView = (
    <ClientsPanelView
      clients={clients}
      filteredClients={filteredClients}
      activeClientId={activeClientId}
      clientQuery={clientQuery}
      clientQueryOptions={clients}
      clientEmptyText={clientEmptyText}
      clientCreationEnabled={clientCreationEnabled}
      onClientQueryChange={setClientQuery}
      onCreateClient={handleCreateClientView}
      onNavigateClient={onNavigateClient}
      runIO={runPanelIO}
      formatTime={formatTime}
    />
  );
  const projectsPanelView = (
    <ProjectsPanelView
      projects={projects}
      filteredProjects={filteredProjects}
      activeProjectId={activeProjectId}
      activeClientId={activeClientId}
      clientQuery={clientQuery}
      projectNameOptions={projectNameOptions}
      projectEmptyText={projectEmptyText}
      onClientQueryChange={setClientQuery}
      onCreateProject={handleCreateProjectView}
      onNavigateProject={onNavigateProject}
      onOpenProjectMenu={handleOpenProjectMenu}
      runIO={runPanelIO}
      formatTime={formatTime}
    />
  );
  const uploadPanelView = (
    <label
      className={`${styles.dropzone} ${isUploading ? styles.dropzoneBusy : ""}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className={styles.fileInput}
        onChange={handleFileInput}
        disabled={Option.fold(activeProjectId, () => true, () => false) || isUploading}
      />
      <div>
        <p className={styles.dropzoneTitle}>Drag & drop files</p>
        <p className={styles.dropzoneSubtitle}>
          {hasActiveProject
            ? "Or click to choose files to upload."
            : "Select a project to enable uploads."}
        </p>
      </div>
    </label>
  );
  const queueEmptyText = hasActiveProject
    ? trimmedDocumentQuery
      ? "No queued documents match your search."
      : "No documents are queued for processing."
    : "Choose a project to see its queue.";
  const documentsEmptyText = hasActiveProject
    ? trimmedDocumentQuery
      ? "No documents match your search."
      : "No documents uploaded yet."
    : "Choose a project to see documents.";
  const emptyQueueView = (
    <div className={styles.emptyDocument}>
      <p className={styles.emptyState}>{queueEmptyText}</p>
    </div>
  );
  const emptyDocumentsView = (
    <div className={styles.emptyDocument}>
      <p className={styles.emptyState}>{documentsEmptyText}</p>
    </div>
  );
  const documentsPanelView = (
    <DocumentsPanelView
      activeClientName={activeClientName}
      isUploadPanelOpen={isUploadPanelOpen}
      uploadPanelView={uploadPanelView}
      hasActiveProject={hasActiveProject}
      supplyStatusRows={supplyStatusRows}
      supplySelectedFileName={supplySelectedFileName}
      supplyErrorMessage={supplyErrorMessage}
      isSupplySubmitting={isSupplySubmitting}
      onSelectSupplyFile={onSelectSupplyFile}
      onRefreshSupplyStatus={onRefreshSupplyStatus}
      documentQuery={documentQuery}
      onDocumentQueryChange={setDocumentQuery}
      documentNameOptions={documentNameOptions}
      emptyQueueView={emptyQueueView}
      emptyDocumentsView={emptyDocumentsView}
      filteredDocuments={filteredDocuments}
      onViewDocument={onViewDocument}
      onDownloadDocument={onDownloadDocument}
      onRemoveDocument={handleRemoveDocument}
      runIO={runPanelIO}
      formatBytes={formatBytes}
      formatTime={formatTime}
    />
  );
  const panelBodyView: Record<ClientsPageView, React.ReactElement> = {
    clients: clientsPanelView,
    projects: projectsPanelView,
    documents: documentsPanelView,
  };
  const createProjectPhase: ClientsPageActionPhase =
    activeView === "projects" && projects.length > 0 ? "visible" : "hidden";
  const uploadPhase: ClientsPageActionPhase =
    activeView === "documents" ? "visible" : "hidden";
  const addClientPhase: ClientsPageActionPhase =
    clientCreationEnabled && activeView === "clients" && clients.length > 0 ? "visible" : "hidden";

  const createProjectModalPhase: ClientsPageModalPhase =
    isCreateProjectModalOpen ? "visible" : "hidden";
  const createClientModalPhase: ClientsPageModalPhase =
    clientCreationEnabled && isCreateClientModalOpen ? "visible" : "hidden";

  const deleteTargetView = (target: DocumentItem) => (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Remove document?</p>
        <p className={styles.modalMessage}>
          This will permanently remove “{target.filename}”.
        </p>
        <div className={styles.modalActions}>
          <button className={styles.modalButton} onClick={() => setDeleteTarget(Option.none())}>
            Cancel
          </button>
          <button
            className={styles.modalButtonDanger}
            onClick={() => {
              IO.run(onDeleteDocument(target.id));
              setDeleteTarget(Option.none());
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );

  const projectMenuView = (menu: ProjectMenu) => (
    <ProjectMenuDropdown
      menu={menu}
      onRename={() => {
        setProjectMenu(Option.none());
        setRenameProjectTitle(menu.title);
        const target = projects.find((project) => project.id === menu.id);
        setRenameTarget(target ? Option.some(target) : Option.none());
        return ok;
      }}
      onDelete={() => {
        setProjectMenu(Option.none());
        setDeleteProjectConfirm("");
        const target = projects.find((project) => project.id === menu.id);
        setDeleteProjectTarget(target ? Option.some(target) : Option.none());
        return ok;
      }}
    />
  );

  const renameTargetView = (target: ProjectItem) => (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Rename project</p>
        <p className={styles.modalMessage}>
          Update the title for {target.name}.
        </p>
        <input
          className={styles.modalInput}
          placeholder="Project title"
          value={renameProjectTitle}
          onChange={(event) => setRenameProjectTitle(event.target.value)}
        />
        <div className={styles.modalActions}>
          <button
            className={styles.modalButton}
            onClick={() => {
              setRenameTarget(Option.none());
              setRenameProjectTitle("");
            }}
          >
            Cancel
          </button>
          <button
            className={styles.modalButtonPrimary}
            onClick={() => {
              IO.run(onRenameProject({ projectId: target.id, title: renameProjectTitle }));
              setRenameTarget(Option.none());
              setRenameProjectTitle("");
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  const deleteProjectTargetView = (target: ProjectItem) => (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Delete project</p>
        <p className={styles.modalMessage}>
          Type “{target.name}” to confirm deletion.
        </p>
        <input
          className={styles.modalInput}
          placeholder="Project title"
          value={deleteProjectConfirm}
          onChange={(event) => setDeleteProjectConfirm(event.target.value)}
        />
        <div className={styles.modalActions}>
          <button
            className={styles.modalButton}
            onClick={() => {
              setDeleteProjectTarget(Option.none());
              setDeleteProjectConfirm("");
            }}
          >
            Cancel
          </button>
          <button
            className={styles.modalButtonDanger}
            onClick={() => {
              if (deleteProjectConfirm !== target.name) {
                return;
              }
              if (!window.confirm("Delete this project?")) {
                return;
              }
              IO.run(onDeleteProject(target.id));
              setDeleteProjectTarget(Option.none());
              setDeleteProjectConfirm("");
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const createProjectModalView = (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Create project</p>
        <p className={styles.modalMessage}>
          Enter a project title for {hasActiveClient ? activeClientName : "this client"}.
        </p>
        <input
          className={styles.modalInput}
          placeholder="Project title"
          value={newProjectTitle}
          onChange={(event) => IO.run(onNewProjectTitleChange(event.target.value))}
        />
        <div className={styles.modalActions}>
          <button
            className={styles.modalButton}
            onClick={() => {
              setIsCreateProjectModalOpen(false);
              IO.run(onNewProjectTitleChange(""));
            }}
          >
            Cancel
          </button>
          <button
            className={styles.modalButtonPrimary}
            disabled={newProjectTitle.trim().length === 0}
            onClick={() => {
              IO.run(onCreateProject(newProjectTitle));
              setIsCreateProjectModalOpen(false);
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );

  const createClientModalView = (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Create client</p>
        <p className={styles.modalMessage}>Enter a client name.</p>
        <input
          className={styles.modalInput}
          placeholder="Client name"
          value={newClientName}
          onChange={(event) => IO.run(onNewClientNameChange(event.target.value))}
        />
        <div className={styles.modalActions}>
          <button
            className={styles.modalButton}
            onClick={() => {
              setIsCreateClientModalOpen(false);
              IO.run(onNewClientNameChange(""));
            }}
          >
            Cancel
          </button>
          <button
            className={styles.modalButtonPrimary}
            disabled={newClientName.trim().length === 0}
            onClick={() => {
              IO.run(onCreateClient(newClientName));
              setIsCreateClientModalOpen(false);
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );

  const headerSubtitleFallback = "Select a project to see documents.";
  const backToClientsButton = (
    <button className={styles.backButton} onClick={() => IO.run(onBackToClients(ok))}>
      &larr; Back to clients
    </button>
  );
  const backToProjectsButton = (
    <button className={styles.backButton} onClick={() => IO.run(onBackToProjects(ok))}>
      &larr; Back to projects
    </button>
  );

  return (
    <ClientsPagePanel
      activeView={activeView}
      activeClientName={activeClientName}
      activeProjectName={activeProjectName}
      activeProjectLastEdited={formatTime(activeProjectLastEdited)}
      hasActiveClient={hasActiveClient}
      headerSubtitleFallback={headerSubtitleFallback}
      backToClientsButton={backToClientsButton}
      backToProjectsButton={backToProjectsButton}
      panelTitleText={panelTitleText[activeView]}
      panelHintText={panelHintText[activeView]}
      createProjectPhase={createProjectPhase}
      uploadPhase={uploadPhase}
      addClientPhase={addClientPhase}
      createProjectButton={createProjectButton}
      uploadButton={uploadButton}
      addClientButton={addClientButton}
      panelBodyView={panelBodyView[activeView]}
      deleteTarget={deleteTarget}
      deleteTargetView={deleteTargetView}
      projectMenu={projectMenu}
      projectMenuView={projectMenuView}
      renameTarget={renameTarget}
      renameTargetView={renameTargetView}
      deleteProjectTarget={deleteProjectTarget}
      deleteProjectTargetView={deleteProjectTargetView}
      createProjectModalPhase={createProjectModalPhase}
      createProjectModalView={createProjectModalView}
      createClientModalPhase={createClientModalPhase}
      createClientModalView={createClientModalView}
    />
  );
};

export default ClientsView;
